import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePresentation } from "@/contexts/PresentationContext";
import {
  Crown, Landmark, Pickaxe, Compass, HandCoins, Users, ChevronRight,
  ShieldAlert, Award, TrendingUp, ArrowRight, MapPin, Sparkles, FileText,
} from "lucide-react";

/**
 * Role-Based Executive Landings — presentation-only.
 * Each landing composes existing endpoints and deep-links to the modules that
 * own the capability. Never creates new business logic.
 */

const ROLES = [
  { key: "president",            label: "President",              icon: Crown,       accent: "#7B2FBE" },
  { key: "vice_president",       label: "Vice President",         icon: Crown,       accent: "#7B2FBE" },
  { key: "minister",             label: "Cabinet Minister",       icon: Award,       accent: "#C99A2E" },
  { key: "permanent_secretary",  label: "Permanent Secretary",    icon: FileText,    accent: "#C99A2E" },
  { key: "commissioner",         label: "Chief Mining Commissioner", icon: Pickaxe,  accent: "#C99A2E" },
  { key: "surveyor_general",     label: "Surveyor General",       icon: Compass,     accent: "#C99A2E" },
  { key: "registrar",            label: "Registrar of Lands",     icon: Landmark,    accent: "#C99A2E" },
  { key: "provincial",           label: "Provincial Executive",   icon: MapPin,      accent: "#4FA3D1" },
  { key: "investor",             label: "Investor",               icon: HandCoins,   accent: "#6BB88A" },
  { key: "citizen",              label: "Citizen",                icon: Users,       accent: "#6BB88A" },
];

const Card = ({ title, accent, children, right, to, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto flex items-center gap-2">
        {right}
        {to && <Link to={to} className="text-[10px] font-mono uppercase text-[#B3C2CC] hover:text-[#F6F8FA] inline-flex items-center gap-1">Open <ChevronRight size={10} /></Link>}
      </div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Metric = ({ label, value, accent = "#C99A2E" }) => (
  <div className="rounded-sm border px-3 py-2" style={{ borderColor: accent + "30", background: "rgba(7,27,43,0.45)" }}>
    <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#7C93A5]">{label}</div>
    <div className="font-display font-bold text-[#F6F8FA] mt-0.5 text-[18px] break-words">{value}</div>
  </div>
);

const useCore = () => {
  const situation = useQuery({ queryKey: ["role-sit"], queryFn: async () => (await api.get("/cadastre360/sovereign/situation-room")).data });
  const perf = useQuery({ queryKey: ["role-perf"], queryFn: async () => (await api.get("/cadastre360/gsros/performance-index")).data });
  const cabinet = useQuery({ queryKey: ["role-cab"], queryFn: async () => (await api.get("/cadastre360/gsros/cabinet-command-centre")).data });
  const risk = useQuery({ queryKey: ["role-risk"], queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data });
  const eco = useQuery({ queryKey: ["role-eco"], queryFn: async () => (await api.get("/cadastre360/sovereign/economy")).data });
  const twin = useQuery({ queryKey: ["role-twin"], queryFn: async () => (await api.get("/cadastre360/sovereign/twin")).data });
  const marketplace = useQuery({ queryKey: ["role-mkt"], queryFn: async () => (await api.get("/cadastre360/sovereign/marketplace")).data });
  return { situation, perf, cabinet, risk, eco, twin, marketplace };
};

const PresidentLanding = () => {
  const { situation, perf, cabinet, risk, eco } = useCore();
  const k = situation.data?.cabinet_kpis || {};
  return (
    <div className="space-y-3" data-testid="landing-president">
      <div className="rounded-sm border p-6 flex items-start gap-4 relative overflow-hidden"
           style={{ borderColor: "#7B2FBE60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(123,47,190,0.28))" }}>
        <Crown size={36} className="text-[#C99A2E] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Presidential Situation Overview</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">His Excellency, the President</h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-3xl">Strategic view of the sovereign resource operating environment.</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Sovereign performance"   value={`${perf.data?.national_score ?? "—"} · ${perf.data?.grade ?? ""}`} accent="#C99A2E" />
        <Metric label="Cabinet attention items" value={cabinet.data?.total_items_flagged ?? "—"} accent="#7B2FBE" />
        <Metric label="Composite risk"          value={risk.data?.composite_score ?? "—"} accent="#E67E7E" />
        <Metric label="Resource revenue YTD"    value={`USD ${Number(eco.data?.national_macro_snapshot_usd_m?.royalties_ytd || 0).toFixed(1)}m`} accent="#4FA3D1" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Cabinet priorities" accent="#7B2FBE" to="/app/gsros">
          {(cabinet.data?.top_priority_items || []).slice(0, 4).map((it) => (
            <div key={it.id} className="border-b border-white/5 py-1.5 text-[11.5px]">
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono mr-2" style={{ background: "#7B2FBE30", color: "#7B2FBE" }}>{it.urgency_score}</span>
              <span className="text-[#F6F8FA]">{it.detail}</span>
              <div className="text-[10px] text-[#7C93A5] font-mono ml-8">{it.portfolio}</div>
            </div>
          ))}
        </Card>
        <Card title="Strategic risks" accent="#E67E7E" to="/app/gsros">
          {(risk.data?.risks || []).slice(0, 5).map((r) => (
            <div key={r.id} className="border-b border-white/5 py-1 text-[11.5px] flex items-center gap-2">
              <span className="font-mono text-[10px] text-[#7C93A5] w-14">{r.id}</span>
              <span className="text-[#F6F8FA] flex-1">{r.risk}</span>
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono"
                    style={{ background: r.impact === "critical" ? "#E67E7E30" : r.impact === "high" ? "#C99A2E30" : "#4FA3D130", color: r.impact === "critical" ? "#E67E7E" : r.impact === "high" ? "#C99A2E" : "#4FA3D1" }}>
                {r.impact}
              </span>
            </div>
          ))}
        </Card>
        <Card title="Provincial ranking" accent="#7B2FBE" to="/app/gsros">
          {(perf.data?.provincial_scores || []).slice(0, 5).map((p) => (
            <div key={p.province} className="flex items-center gap-2 py-1 border-b border-white/5 text-[11.5px]">
              <div className="w-8 font-mono text-[10px] text-[#7C93A5]">#{p.rank}</div>
              <div className="flex-1 text-[#F6F8FA]">{p.province}</div>
              <div className="font-mono text-[#F6F8FA] w-14 text-right">{p.score}</div>
            </div>
          ))}
        </Card>
        <Card title="Today's executive brief" accent="#C99A2E" to="/app/gsros">
          <div className="text-[11.5px] text-[#DCE3E8] leading-relaxed">
            Generate a cabinet-grade briefing pack (PDF + DOCX + QR) from GSROS Executive Brief Generator. AI-narrated via Emergent LLM.
          </div>
          <Link to="/app/gsros" className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono uppercase text-[#C99A2E]">
            Generate briefing <ArrowRight size={11} />
          </Link>
        </Card>
      </div>
    </div>
  );
};

const MinisterLanding = ({ portfolio = "Mines & Mining Development" }) => {
  const { cabinet, perf, situation, risk } = useCore();
  const portfolioItems = (cabinet.data?.top_priority_items || []).filter((it) => it.portfolio?.toLowerCase().includes("min")).slice(0, 6);
  const k = situation.data?.cabinet_kpis || {};
  return (
    <div className="space-y-3" data-testid="landing-minister">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#C99A2E60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(201,154,46,0.25))" }}>
        <Award size={34} className="text-[#C99A2E] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Ministerial Portfolio Situation Room</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Honourable Minister · {portfolio}</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Priority decisions" value={portfolioItems.length} accent="#7B2FBE" />
        <Metric label="Applications open" value={k.applications_open ?? "—"} accent="#6BB88A" />
        <Metric label="Mining conflicts" value={k.mining_conflicts ?? "—"} accent="#E67E7E" />
        <Metric label="Revenue outstanding" value={`USD ${Number(k.revenue_outstanding_usd || 0).toLocaleString()}`} accent="#4FA3D1" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title={`${portfolio} — priority queue`} accent="#C99A2E" to="/app/gsros">
          {portfolioItems.map((it) => (
            <div key={it.id} className="border-b border-white/5 py-1.5 text-[11.5px]">
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono mr-2" style={{ background: "#C99A2E30", color: "#C99A2E" }}>{it.urgency_score}</span>
              <span className="text-[#F6F8FA]">{it.detail}</span>
              <div className="text-[11px] text-[#DCE3E8] mt-0.5 ml-8">→ {it.recommended_action}</div>
            </div>
          ))}
        </Card>
        <Card title="Compliance & inspections" accent="#6BB88A" to="/app/compliance">
          <div className="grid grid-cols-2 gap-2">
            <Metric label="Inspections due" value={k.inspections_due ?? "—"} accent="#6BB88A" />
            <Metric label="Disputes open" value={k.disputes_open ?? "—"} accent="#E67E7E" />
          </div>
        </Card>
        <Card title="Portfolio performance trend" accent="#C99A2E" to="/app/gsros">
          <div className="text-[11.5px] text-[#DCE3E8]">National performance: <span className="font-mono text-[#C99A2E]">{perf.data?.national_score} · {perf.data?.grade}</span></div>
          <div className="text-[11.5px] text-[#DCE3E8] mt-1">Mining governance score: <span className="font-mono text-[#F6F8FA]">{(perf.data?.dimensions || []).find((d) => d.key === "mining_governance")?.score}</span></div>
        </Card>
        <Card title="Portfolio risks" accent="#E67E7E" to="/app/gsros">
          {(risk.data?.risks || []).filter((r) => r.owner?.toLowerCase().includes("min")).slice(0, 4).map((r) => (
            <div key={r.id} className="border-b border-white/5 py-1 text-[11.5px]">
              <span className="text-[#F6F8FA]">{r.risk}</span>
              <span className="text-[10px] text-[#7C93A5] font-mono ml-2">{r.owner}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

const InvestorLanding = () => {
  const { marketplace, eco } = useCore();
  const m = marketplace.data || {};
  return (
    <div className="space-y-3" data-testid="landing-investor">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#6BB88A60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(107,184,138,0.25))" }}>
        <HandCoins size={34} className="text-[#6BB88A] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#6BB88A]">Investor Workspace</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">National Investment Opportunities</h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2">Explore mining, land, industrial-park and strategic-mineral opportunities.</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Mining opportunities" value={m.opportunity_summary?.mining_opportunities ?? "—"} accent="#C99A2E" />
        <Metric label="Land opportunities" value={m.opportunity_summary?.land_opportunities ?? "—"} accent="#C99A2E" />
        <Metric label="Strategic minerals" value={m.opportunity_summary?.strategic_minerals_open ?? "—"} accent="#7B2FBE" />
        <Metric label="Beneficiation PPPs" value={m.opportunity_summary?.beneficiation_ppp_projects ?? "—"} accent="#6BB88A" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Strategic minerals open" accent="#7B2FBE" to="/portal/mining-investor">
          <div className="flex flex-wrap gap-1">
            {(m.strategic_minerals_open || []).map((s) => (
              <span key={s} className="rounded-sm px-2 py-0.5 text-[11px] font-mono" style={{ background: "#7B2FBE30", color: "#7B2FBE" }}>{s}</span>
            ))}
          </div>
        </Card>
        <Card title="Beneficiation opportunities" accent="#6BB88A" to="/portal/mining-investor">
          {(m.beneficiation_opportunities || []).slice(0, 4).map((b, i) => (
            <div key={`b-${i}`} className="border-b border-white/5 py-1 text-[11.5px]">
              <div className="text-[#F6F8FA]">{b.name}</div>
              <div className="text-[10.5px] text-[#7C93A5] font-mono">{b.sector} · USD {b.capex_usd_m}m · {b.location}</div>
            </div>
          ))}
        </Card>
        <Card title="SEZ index" accent="#4FA3D1" to="/portal/mining-investor">
          {(m.sez_index || []).map((s) => (
            <div key={s.name} className="border-b border-white/5 py-1 text-[11.5px] flex items-center gap-2">
              <div className="text-[#F6F8FA] flex-1">{s.name}</div>
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono" style={{ background: s.status === "operational" ? "#6BB88A30" : "#C99A2E30", color: s.status === "operational" ? "#6BB88A" : "#C99A2E" }}>{s.status}</span>
            </div>
          ))}
        </Card>
        <Card title="Public investor portal" accent="#C99A2E">
          <div className="text-[11.5px] text-[#DCE3E8] leading-relaxed">
            Access the sovereign investor portal for due-diligence packs, ZIDA contacts, and interactive GIS opportunity maps.
          </div>
          <a href="/portal/mining-investor" target="_blank" rel="noreferrer"
             className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono uppercase text-[#C99A2E]">
            Open portal <ArrowRight size={11} />
          </a>
        </Card>
      </div>
    </div>
  );
};

const CitizenLanding = () => {
  return (
    <div className="space-y-3" data-testid="landing-citizen">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#6BB88A60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(107,184,138,0.25))" }}>
        <Users size={34} className="text-[#6BB88A] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#6BB88A]">Citizen Workspace</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Public Services</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "Application tracking",    to: "/app/portal" },
          { label: "Public certificate check",to: "/certificates" },
          { label: "Public search",           to: "/portal/search" },
          { label: "Public maps",             to: "/portal/map" },
        ].map((s) => (
          <Link key={s.to} to={s.to} className="rounded-sm border border-white/10 p-4 text-[12px] text-[#F6F8FA] hover:border-[#6BB88A]/60">
            {s.label}
            <ArrowRight size={11} className="inline-block ml-1" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const CommissionerLanding = () => {
  const { situation, cabinet } = useCore();
  const k = situation.data?.cabinet_kpis || {};
  const items = (cabinet.data?.top_priority_items || []).filter((it) => /mine|licen|royalt|min/i.test(it.portfolio + " " + it.kind)).slice(0, 6);
  return (
    <div className="space-y-3" data-testid="landing-commissioner">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#C99A2E60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(201,154,46,0.25))" }}>
        <Pickaxe size={34} className="text-[#C99A2E] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Mining Command Centre</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Chief Mining Commissioner</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Active mineral rights" value={k.mining_active ?? "—"} accent="#C99A2E" />
        <Metric label="Pending" value={k.mining_pending ?? "—"} accent="#4FA3D1" />
        <Metric label="Conflicts" value={k.mining_conflicts ?? "—"} accent="#E67E7E" />
        <Metric label="Inspections due" value={k.inspections_due ?? "—"} accent="#6BB88A" />
      </div>
      <Card title="Mining priority queue" accent="#C99A2E" to="/app/mining">
        {items.length === 0 && <div className="text-[11px] text-[#7C93A5] italic">No mining-specific items in the priority queue.</div>}
        {items.map((it) => (
          <div key={it.id} className="border-b border-white/5 py-1.5 text-[11.5px]">
            <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono mr-2" style={{ background: "#C99A2E30", color: "#C99A2E" }}>{it.urgency_score}</span>
            <span className="text-[#F6F8FA]">{it.detail}</span>
            <div className="text-[11px] text-[#DCE3E8] mt-0.5 ml-8">→ {it.recommended_action}</div>
          </div>
        ))}
      </Card>
    </div>
  );
};

const ProvincialLanding = () => {
  const perf = useQuery({ queryKey: ["prov-list"], queryFn: async () => (await api.get("/cadastre360/gsros/provincial-dashboards")).data });
  const provinces = perf.data?.dashboards || [];
  return (
    <div className="space-y-3" data-testid="landing-provincial">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#4FA3D160", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(79,163,209,0.25))" }}>
        <MapPin size={34} className="text-[#4FA3D1] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#4FA3D1]">Provincial Executive Workspace</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Provincial Executive</h1>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {provinces.map((p) => (
          <div key={p.province} className="rounded-sm border border-white/10 p-3" style={{ background: "rgba(11,53,88,0.35)" }}>
            <div className="flex items-baseline gap-2">
              <div className="font-display font-bold text-[#F6F8FA] text-[15px]">{p.province}</div>
              <div className="ml-auto font-mono text-[10px] text-[#7C93A5]">#{p.national_rank}</div>
            </div>
            <div className="mt-1 text-[11px] text-[#DCE3E8]">Executive score <span className="font-mono text-[#C99A2E]">{p.executive_score}</span></div>
            <div className="mt-1 grid grid-cols-3 gap-1 text-[10.5px] font-mono">
              <div>Land <span className="text-[#F6F8FA]">{p.land?.parcels}</span></div>
              <div>Mining <span className="text-[#F6F8FA]">{p.mining?.active_rights}</span></div>
              <div>Risks <span className="text-[#E67E7E]">{p.conflicts}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RoleLanding = () => {
  const { role, setRole } = usePresentation();
  const active = ROLES.find((r) => r.key === role) || ROLES[4];

  const renderLanding = () => {
    switch (active.key) {
      case "president":
      case "vice_president":       return <PresidentLanding />;
      case "minister":
      case "permanent_secretary":  return <MinisterLanding portfolio="Mines, Lands & Survey" />;
      case "commissioner":         return <CommissionerLanding />;
      case "surveyor_general":     return <CommissionerLanding />;
      case "registrar":            return <CommissionerLanding />;
      case "provincial":           return <ProvincialLanding />;
      case "investor":             return <InvestorLanding />;
      case "citizen":              return <CitizenLanding />;
      default:                     return <CommissionerLanding />;
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="page-role-landing">
      <div className="rounded-sm border border-white/10 p-3 flex flex-wrap items-center gap-2" style={{ background: "rgba(11,53,88,0.35)" }} data-testid="role-switcher">
        <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#C99A2E]">Executive Role Landing</div>
        <div className="ml-auto flex flex-wrap gap-1">
          {ROLES.map((r) => (
            <button key={r.key} onClick={() => setRole(r.key)}
                    data-testid={"role-btn-" + r.key}
                    className="rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1"
                    style={{
                      background: role === r.key ? r.accent + "40" : "transparent",
                      color: role === r.key ? "#F6F8FA" : "#B3C2CC",
                      border: role === r.key ? `1px solid ${r.accent}` : "1px solid rgba(255,255,255,0.08)",
                    }}>
              <r.icon size={10} style={{ color: r.accent }} /> {r.label}
            </button>
          ))}
        </div>
      </div>
      {renderLanding()}
    </div>
  );
};

export default RoleLanding;
